import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { addDays, dayLabel, teamGrid } from '@kronos/domain'
import type { LocalDate } from '@kronos/domain'
import { repository } from '../../data'
import { problemOf } from '../hours/time'
import { EmptyState, Panel } from '../primitives'
import { BackedOutList, PendingList } from './PendingList'
import { RequestForm } from './RequestForm'
import type { Ask } from './RequestForm'
import { TeamDayList } from './TeamDayList'
import { TeamGrid } from './TeamGrid'
import { teamKey } from './shared'

/**
 * People › Schedule: every technician's fortnight, and asking one of them in.
 *
 * A grid on a desk (technicians down, days across, today first) and the same
 * data grouped by day on a phone. Clicking a day opens the request form with
 * that person, that day and the hours they said they can do already filled in.
 */
export function TeamScheduleView(): JSX.Element {
  const [from, setFrom] = useState<LocalDate | null>(null)
  const [asking, setAsking] = useState<Ask | null>(null)
  const team = useQuery({
    queryKey: teamKey(from),
    queryFn: () => repository.teamSchedule(from ?? undefined),
    // Keep last fortnight on screen while ‹ › fetches the next, so the grid
    // does not collapse to "Loading…" and back on every press.
    placeholderData: keepPreviousData,
  })

  if (team.isPending) return <p className="muted">Loading…</p>
  if (team.isError) return <p className="critical">{problemOf(team.error)}</p>
  const data = team.data
  const grid = teamGrid({ ...data, timeZone: data.timeZone })
  const last = addDays(data.from, data.days - 1)
  const first = data.technicians[0]

  return (
    <>
      <div className="toolbar">
        <div className="period-nav">
          <button aria-label="Previous week" onClick={() => setFrom(addDays(data.from, -7))}>
            ‹
          </button>
          <span className="period-nav__label">
            {dayLabel(data.from)} – {dayLabel(last)}
          </span>
          <button aria-label="Next week" onClick={() => setFrom(addDays(data.from, 7))}>
            ›
          </button>
        </div>
        {data.from !== data.today ? (
          <button onClick={() => setFrom(null)}>
            Today
          </button>
        ) : null}
        <span className="toolbar__spacer" />
        <Legend />
        {first ? (
          <button className="primary" onClick={() => setAsking({ technicianId: first.id, date: data.today })}>
            Ask someone in
          </button>
        ) : null}
      </div>

      {data.technicians.length === 0 ? (
        <Panel>
          <EmptyState icon="office" title="No technicians yet">
            Give someone the technician role under Team, and they can set their availability here.
          </EmptyState>
        </Panel>
      ) : (
        <>
          <TeamGrid grid={grid} today={data.today} timeZone={data.timeZone} onPick={setAsking} />
          <TeamDayList grid={grid} today={data.today} timeZone={data.timeZone} onPick={setAsking} />
        </>
      )}

      <BackedOutList requests={data.backedOut} timeZone={data.timeZone} />
      <PendingList pending={data.pending} timeZone={data.timeZone} />

      {asking ? <RequestForm team={data} ask={asking} onClose={() => setAsking(null)} /> : null}
    </>
  )
}

function Legend(): JSX.Element {
  return (
    <span className="sched-legend" aria-label="Key">
      <span className="sched-legend__item">
        <span className="sched-swatch sched-swatch--available" /> Available
      </span>
      <span className="sched-legend__item">
        <span className="sched-swatch sched-swatch--pending" /> Asked
      </span>
      <span className="sched-legend__item">
        <span className="sched-swatch sched-swatch--accepted" /> Coming in
      </span>
    </span>
  )
}
